import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import { useUpdateRemindersMutation } from "@/data/queries"
import { DayOfWeek, ReminderDayTime } from "@/model/Reminders"
import cn from "@/utils/cn"
import { formatTime } from "@/utils/date-utils"
import { LocalTime } from "@js-joda/core"
import { differenceWith, groupBy, sortBy } from "lodash"
import { useState } from "react"
import { toast } from "sonner"

type RemindersListProps = {
  bloomlifeId: string
  reminders?: ReminderDayTime[]
}

const RemindersList = ({ bloomlifeId, reminders = [] }: RemindersListProps) => {
  const { isPending: isUpdatingReminders, mutateAsync: updateReminders } =
    useUpdateRemindersMutation(bloomlifeId)
  const [deletedReminders, setDeletedReminders] = useState<ReminderDayTime[]>(
    []
  )

  const openDeleteReminderModal = (reminders: ReminderDayTime[]) =>
    setDeletedReminders(reminders)
  const closeDeleteReminderModal = () => setDeletedReminders([])

  const groupedReminders = groupBy(reminders, (reminder) =>
    reminder.time.toString()
  )

  const remindersList = sortBy(
    Object.entries(groupedReminders).map(([time, group]) => ({
      time: LocalTime.parse(time),
      days: group.map(({ dayOfWeek }) => dayOfWeek),
      type: group[0].type,
    })),
    (reminder) => reminder.time.toString()
  )

  const deleteReminder = async () => {
    const filteredReminders = differenceWith(
      reminders,
      deletedReminders,
      (a, b) => a.dayOfWeek === b.dayOfWeek && a.time.equals(b.time)
    )

    try {
      await updateReminders({
        reminderType: "measurement",
        reminders: filteredReminders,
      })
      setDeletedReminders([])
      toast.custom((toastId) => (
        <Toast
          toastId={toastId}
          description="Reminders deleted successfully."
        />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while deleting the reminder. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <div>
      {remindersList.length === 0 ? (
        <p className="italic text-primary/70">This patient has no reminders.</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4">
            <h4 className="font-bold leading-6 tracking-wide text-primary/90">
              Time
            </h4>
            <h4 className="font-bold leading-6 tracking-wide text-primary/90">
              Days
            </h4>
          </div>
          {remindersList.map(({ time, days }) => (
            <div
              className="mt-2 grid grid-cols-4 items-center gap-2"
              key={time.toString()}
            >
              <p>{formatTime(time, "hh:mm a")}</p>
              <div className="flex gap-2 pr-3">
                {weekdaysList.map((day) => (
                  <p
                    key={day}
                    className={cn(
                      "px-1 text-[14px] text-primary/60",
                      days.includes(day)
                        ? "rounded-md bg-gray-200 font-extrabold text-primary"
                        : ""
                    )}
                  >
                    {day[0].toUpperCase()}
                  </p>
                ))}
              </div>
              <div
                className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg border-2 border-gray-200"
                onClick={() => {
                  const reminders = days.map((day) => ({
                    dayOfWeek: day,
                    time,
                    type: "measurement",
                  })) as ReminderDayTime[]
                  openDeleteReminderModal(reminders)
                }}
              >
                <span className="material-icons text-[22px] text-primary">
                  close
                </span>
              </div>
            </div>
          ))}
        </>
      )}

      <Dialog
        title="Delete reminder"
        isOpen={deletedReminders.length > 0}
        className="max-w-md"
        onClose={closeDeleteReminderModal}
      >
        <p>Are you sure you want to delete this reminder?</p>
        <div className="mt-8 flex justify-end gap-3">
          <Button
            variant="neutral"
            className="px-6"
            onClick={closeDeleteReminderModal}
            disabled={isUpdatingReminders}
          >
            No
          </Button>
          <Button
            variant="primary"
            className="flex items-center gap-3 px-6"
            onClick={deleteReminder}
            disabled={isUpdatingReminders}
          >
            {isUpdatingReminders && <Spinner className="size-4" />}
            Yes
          </Button>
        </div>
      </Dialog>
    </div>
  )
}

export default RemindersList

const weekdaysList: DayOfWeek[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]
